// @djsem/ui — DJSEM API client (fix report Phase 3 / Story 4).
//
// A single typed client the Electron renderer (and browser POC) use to consume the worker's
// routes, so screens are data-driven instead of reading stubs. Framework-free: it wraps
// fetch and returns the canonical DJSEMApiResponse envelope. React hooks live in apiHooks.ts.
//
// The client never stores secrets. Session auth rides on the HttpOnly cookie set by the
// worker at /api/auth/login (credentials: "include"), so tokens are never in JS.

/** Successful API result: the envelope's `data` payload for a request that resolved OK. */
export interface DJSEMApiOk<T> { ok: true; data: T; }
/** Failed API result: a canonical `{code, message}` error, normalized from the worker's response. */
export interface DJSEMApiErr { ok: false; error: { code: string; message: string }; }
/** The canonical envelope every {@link DJSEMApiClient} request resolves to — either ok-with-data or a typed error. */
export type DJSEMApiResult<T> = DJSEMApiOk<T> | DJSEMApiErr;

/** Construction options for {@link DJSEMApiClient}. */
export interface DJSEMApiClientOptions {
  /** Absolute base URL of the DJSEM worker API (trailing slash is stripped). */
  baseUrl: string;
  /** Injected for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/**
 * Typed client for the DJSEM worker's HTTP API. Wraps `fetch`, normalizes every response into
 * the canonical {@link DJSEMApiResult} envelope, and never stores secrets — session auth rides
 * on the HttpOnly cookie set by `/api/auth/login` (`credentials: "include"`), so tokens never
 * touch JS. One method per worker route; see apiHooks.ts for the React-facing wrappers.
 */
export class DJSEMApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: DJSEMApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    // A bare `fetch` reference loses its receiver — real browsers throw "Failed to execute
    // 'fetch' on 'Window': Illegal invocation" the moment it's invoked as `this.fetchImpl(...)`
    // instead of `window.fetch(...)`, since fetch's WebIDL binding checks the call receiver.
    // Only ever caught by actually running in a real browser — every existing test injects an
    // explicit fetchImpl mock, so this was invisible to the suite. Bind explicitly so the
    // default path (no fetchImpl override) works outside tests too.
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<DJSEMApiResult<T>> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.baseUrl + path, {
        method,
        credentials: "include",
        headers: body !== undefined ? { "content-type": "application/json" } : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (e) {
      return { ok: false, error: { code: "network_error", message: (e as Error).message } };
    }
    if (res.status === 401) return { ok: false, error: { code: "unauthenticated", message: "Not signed in" } };
    if (res.status === 403) return { ok: false, error: { code: "forbidden", message: "Not permitted" } };
    let json: unknown;
    try { json = await res.json(); } catch { return { ok: false, error: { code: "bad_response", message: `Non-JSON response (${res.status})` } }; }
    // Worker returns either {ok:false,error} or a bare payload; normalize both.
    if (json && typeof json === "object" && "ok" in json && (json as { ok: unknown }).ok === false) {
      return json as DJSEMApiErr;
    }
    if (!res.ok) return { ok: false, error: { code: `http_${res.status}`, message: `Request failed (${res.status})` } };
    return { ok: true, data: json as T };
  }

  // ---- auth ----
  login(email: string, password: string) { return this.request<{ id: string; email: string }>("POST", "/api/auth/login", { email, password }); }
  logout() { return this.request<{ ok: boolean }>("POST", "/api/auth/logout"); }
  // NOTE: the worker's actual response shape is {authenticated, user} (see handlers.ts's `me`
  // route) — this previously mistyped it as the bare user object, a latent mismatch never
  // caught because nothing called me() yet (the UI hardcoded a placeholder user; see
  // workspaceApp.tsx). Fixed here as part of wiring real plan/credits (Prompt 03B).
  me() {
    return this.request<{
      authenticated: boolean;
      user: { id: string; email: string; role: string; planLevel: string; renderCreditsRemaining: number } | null;
    }>("GET", "/api/me");
  }
  health() { return this.request<{ ok: boolean; environment: string }>("GET", "/api/health"); }
  // Offline desktop entitlement token (task #45, Phase C) — desktop-only consumer (see
  // platform.ts's offlineEntitlement); 501 (not_configured) until a signing keypair is set.
  getEntitlementToken() { return this.request<{ ok: boolean; token: unknown }>("GET", "/api/entitlement/token"); }

  // ---- perpetual desktop license (task #45, Phase E) — desktop-only consumer (see
  // platform.ts's license bridge); purchase/activate 501 (not_configured) until a price/signing
  // keypair is set server-side. ----
  purchaseLicense(email: string, tier: string) {
    return this.request<{ ok: boolean; licenseId: string; checkoutUrl: string; mode: "live" | "test" }>("POST", "/api/license/purchase", { email, tier });
  }
  getLicenseStatus(licenseId: string) {
    return this.request<{ ok: boolean; license: { id: string; licenseKey: string; tier: string; status: string; activated: boolean } }>("GET", `/api/license/${licenseId}`);
  }
  activateLicense(licenseKey: string, machineId: string) {
    return this.request<{ ok: boolean; token: unknown }>("POST", "/api/license/activate", { licenseKey, machineId });
  }
  /** Opportunistic, unauthenticated online revocation check — see perpetualLicenseToken.ts for why this can't be real-time. */
  getLicenseRevocationStatus(licenseId: string) {
    return this.request<{ ok: boolean; status: string }>("GET", `/api/license/${licenseId}/status`);
  }

  // ---- admin console (68) — license issuance/revocation, used by apps/admin ----
  adminLicensesList() {
    return this.request<{ items: { id: string; licenseKey: string; email: string; tier: string; status: string; machineId: string | null; activatedAt: string | null; createdAt: string }[] }>("GET", "/api/admin/licenses");
  }
  adminLicenseIssue(email: string, tier: string) {
    return this.request<{ ok: boolean; licenseId: string; licenseKey: string }>("POST", "/api/admin/licenses", { email, tier });
  }
  adminLicenseRevoke(licenseId: string) {
    return this.request<{ ok: boolean; revoked: boolean }>("POST", `/api/admin/licenses/${licenseId}/revoke`);
  }
  adminLicenseResetMachine(licenseId: string) {
    return this.request<{ ok: boolean; reset: boolean }>("POST", `/api/admin/licenses/${licenseId}/reset-machine`);
  }

  // ---- library ----
  listTracks() { return this.request<{ items: unknown[] }>("GET", "/api/tracks"); }

  // ---- notifications / tasks (62) ----
  listNotifications() { return this.request<{ items: unknown[] }>("GET", "/api/notifications"); }
  listTasks() { return this.request<{ items: unknown[] }>("GET", "/api/tasks"); }

  // ---- rights ledger (65) ----
  listRights() { return this.request<{ items: unknown[] }>("GET", "/api/rights"); }

  // ---- jobs (30) ----
  listJobs() { return this.request<{ items: unknown[] }>("GET", "/api/jobs"); }

  // ---- sets + cues + snapshots (09/12/15) ----
  listSets() { return this.request<{ items: { id: string; title: string; reviewStatus: string }[] }>("GET", "/api/sets"); }
  getSet(id: string) { return this.request<{ id: string; title: string; payload: unknown }>("GET", `/api/sets/${id}`); }
  createSet(title: string, payload?: unknown) { return this.request<{ id: string; title: string }>("POST", "/api/sets", { title, payload }); }
  updateSet(id: string, patch: { title?: string; payload?: unknown }) { return this.request<{ ok: boolean }>("PUT", `/api/sets/${id}`, patch); }
  deleteSet(id: string) { return this.request<{ ok: boolean }>("DELETE", `/api/sets/${id}`); }
  snapshotSet(id: string) { return this.request<{ ok: boolean; snapshotId: string; hash: string; immutable: boolean }>("POST", `/api/sets/${id}/snapshot`); }
  listCues(trackId: string) { return this.request<{ items: unknown[] }>("GET", `/api/tracks/${trackId}/cues`); }
  upsertCue(trackId: string, cue: { id?: string; positionMs: number; label?: string; intent?: string; color?: string }) { return this.request<{ id: string }>("POST", `/api/tracks/${trackId}/cues`, cue); }
  deleteCue(id: string) { return this.request<{ ok: boolean }>("DELETE", `/api/cues/${id}`); }

  // ---- share links (63) — server mints the token; returned once ----
  createShareLink(input: { resourceType: string; resourceId: string; accessMode?: string; expiresAt?: string }) {
    return this.request<{ id: string; token: string; shareUrl: string; accessMode: string }>("POST", "/api/share-links", input);
  }

  // ---- RBAC (28) ----
  listOrganisations() { return this.request<{ items: { organisationId: string; role: string }[] }>("GET", "/api/organisations"); }
  createOrganisation(name: string) { return this.request<{ id: string; name: string; role: string }>("POST", "/api/organisations", { name }); }
  listProjects() { return this.request<{ items: { projectId: string; role: string }[] }>("GET", "/api/projects"); }

  // ---- platform import/export (04A-04H) ----
  importSpotify(playlistUrl: string) { return this.request<unknown>("POST", "/api/import/spotify", { playlistUrl }); }
  importSoundcloud(playlistUrl: string) { return this.request<unknown>("POST", "/api/import/soundcloud", { playlistUrl }); }
  importRekordbox(xml: string) {
    return this.request<{
      ok: boolean; tracks: Array<{ id: string; type: string; label?: string }>; sets: Array<{ id: string; type: string; label?: string }>;
      report: { tracksCreated: number; tracksUpdated: number; playlistsCreated: number; warnings: string[]; errors: string[] };
      sourceLibraryMutated: false;
    }>("POST", "/api/import/rekordbox", { xml });
  }
  exportRekordbox(input: {
    setIds: string[]; outputFilename: string; exportMode: "playlist_only" | "playlist_with_track_refs" | "playlist_with_cues" | "full_collection_subset";
    includeTrackMetadata: boolean; includeCues: boolean; includeOnlyApprovedCues: boolean;
    sets: unknown[]; tracksById: Record<string, unknown>; cuesByTrackId?: Record<string, unknown[]>;
    userConfirmedNoOverwrite: boolean; userConfirmedCueExport?: boolean;
  }) {
    return this.request<{
      ok: boolean; xml: string; xmlHash: string; warnings: string[];
      manifest: { exportId: string; trackCount: number; playlistCount: number; cueCount: number; warnings: string[]; errors: string[] };
      overwritesSourceLibrary: false;
    }>("POST", "/api/export/rekordbox", input);
  }

  // ---- value-add compute (40-53) ----
  valueAdd(feature: string, payload: unknown) { return this.request<unknown>("POST", `/api/value-add/${feature}`, payload); }

  // ---- gig / CRM / bookings (43/48/51) ----
  listEventBriefs() { return this.request<{ items: { id: string; title: string; eventType: string; status: string }[] }>("GET", "/api/event-briefs"); }
  createEventBrief(input: { title: string; eventType: string; musicRequirements?: unknown; deliverables?: unknown[] }) { return this.request<{ id: string }>("POST", "/api/event-briefs", input); }
  approveEventBrief(id: string) { return this.request<{ ok: boolean; status: string }>("POST", `/api/event-briefs/${id}/approve`); }
  listGigPacks() { return this.request<{ items: { id: string; title: string; gigPackType: string }[] }>("GET", "/api/gig-packs"); }
  createGigPack(input: { title: string; gigPackType: string; projectId?: string; eventBriefId?: string }) { return this.request<{ id: string }>("POST", "/api/gig-packs", input); }
  listCrmContacts() { return this.request<{ items: { id: string; name: string; kind: string }[] }>("GET", "/api/crm/contacts"); }
  createCrmContact(input: { name: string; kind: string; email?: string; phone?: string }) { return this.request<{ id: string }>("POST", "/api/crm/contacts", input); }
  listBookings() { return this.request<{ items: { id: string; title: string; status: string; eventDate?: string }[] }>("GET", "/api/bookings"); }
  createBooking(input: { title: string; contactId?: string; eventDate?: string }) { return this.request<{ id: string }>("POST", "/api/bookings", input); }
  setBookingStatus(id: string, status: string) { return this.request<{ ok: boolean; status: string }>("POST", `/api/bookings/${id}/status`, { status }); }

  // ---- privacy (69) ----
  exportMyData() { return this.request<unknown>("GET", "/api/privacy/export"); }
  listPrivacyRequests() { return this.request<{ items: unknown[] }>("GET", "/api/privacy/requests"); }

  // ---- admin console (68) — used by apps/admin, the separate ops-only frontend ----
  adminModerationQueue() { return this.request<{ items: unknown[] }>("GET", "/api/admin/moderation"); }
  adminModerationDecide(id: string, decision: "approved" | "rejected", reason?: string) {
    return this.request<{ ok: boolean; decision: string }>("POST", `/api/admin/moderation/${id}/decide`, { decision, reason });
  }
  adminRevokeLink(id: string) { return this.request<{ ok: boolean; revoked: boolean }>("POST", `/api/admin/links/${id}/revoke`); }
  adminAuditList(subject?: string) { return this.request<{ items: unknown[] }>("GET", `/api/admin/audit${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`); }
  adminAiProvidersList() { return this.request<{ items: unknown[] }>("GET", "/api/ai/providers"); }
  adminAiProviderUpsert(input: { providerId: string; label: string; status: string; credentialConfigured: boolean; models: unknown[] }) {
    return this.request<{ ok: boolean; providerId: string }>("POST", "/api/ai/providers", input);
  }

  // ---- AI Cue Director (97) — suggest-only, approval before export ----
  listCueModels() { return this.request<{ items: { providerId: string; label: string; productionEnabled: boolean }[] }>("GET", "/api/ai/cue-models"); }
  validateCueModel(modelConfig: unknown) { return this.request<{ ok: boolean; issues: unknown[]; productionEnableable: boolean }>("POST", "/api/ai/cue-models/validate", { modelConfig }); }
  planCueSuggestions(input: { sourceType: string; requestedCueTypes: string[]; trackId?: string; setId?: string; safetyMode?: string }) { return this.request<{ requestId: string; creditEstimate: number; safetyMode: string }>("POST", "/api/ai/cue-suggestions/plan", input); }
  runCueSuggestions(requestId: string, opts?: { useStub?: boolean; fixture?: unknown[] }) { return this.request<{ requestId: string; status: string; suggestionCount: number; warnings: string[] }>("POST", "/api/ai/cue-suggestions/run", { requestId, ...opts }); }
  getCueSuggestions(requestId: string) { return this.request<{ requestId: string; items: unknown[] }>("GET", `/api/ai/cue-suggestions/${requestId}`); }
  getCueReport(requestId: string) { return this.request<{ requestId: string; total: number; byStatus: Record<string, number> }>("GET", `/api/ai/cue-suggestions/${requestId}/report`); }
  approveCue(suggestionId: string, opts?: { acknowledgeLowConfidence?: boolean; confirmSensitive?: boolean }) { return this.request<{ ok: boolean; approvalStatus: string }>("POST", `/api/ai/cue-suggestions/${suggestionId}/approve`, opts ?? {}); }
  rejectCue(suggestionId: string) { return this.request<{ ok: boolean }>("POST", `/api/ai/cue-suggestions/${suggestionId}/reject`); }
  editCue(suggestionId: string, patch: Record<string, unknown>) { return this.request<{ ok: boolean; approvalStatus: string }>("PATCH", `/api/ai/cue-suggestions/${suggestionId}`, patch); }
  exportApprovedCues(requestId: string) { return this.request<{ ok: boolean; snapshotId: string; hash: string; exportedCount: number; immutable: boolean }>("POST", "/api/ai/cue-suggestions/export-approved", { requestId }); }

  // ---- AI Stem Separation Studio (135/19A, extended by 136/140/141) ----
  estimateStemSeparation(input: { trackId: string; sourceAudioRef: string; requestedMode?: string; outputFormat?: string; qualityMode?: string; preferredModelId?: string; sourceDurationMs?: number }) {
    return this.request<{
      requestId: string; estimatedCreditCost: number; estimatedDurationSeconds?: number; estimatedOutputStorageBytes?: number;
      providerId?: string; warnings: string[]; blockers: string[];
      resolvedModelId?: string; modelResolutionWarnings: string[]; modelResolutionBlockers: string[];
    }>("POST", "/api/stems/separation/estimate", input);
  }
  createStemSeparationRequest(input: {
    trackId: string; sourceAudioRef: string; requestedMode: string; outputFormat: string; qualityMode: string;
    userConfirmedRightsWarning: boolean; userConfirmedCreditUsage: boolean;
    projectId?: string; sourceAudioHash?: string; providerId?: string; modelId?: string;
  }) {
    return this.request<{ ok: boolean; requestId: string }>("POST", "/api/stems/separation/requests", input);
  }
  getStemSeparationRequest(requestId: string) { return this.request<{ request: unknown }>("GET", `/api/stems/separation/requests/${requestId}`); }
  createStemSeparationJob(requestId: string, preferredModelId?: string) {
    return this.request<{ job: unknown; modelResolutionWarnings: string[] }>("POST", "/api/stems/separation/jobs", { requestId, preferredModelId });
  }
  listStemSeparationJobs(trackId?: string) {
    return this.request<{ jobs: unknown[] }>("GET", `/api/stems/separation/jobs${trackId ? `?trackId=${encodeURIComponent(trackId)}` : ""}`);
  }
  getStemSeparationJob(jobId: string) { return this.request<{ job: unknown; outputs: unknown[] }>("GET", `/api/stems/separation/jobs/${jobId}`); }
  cancelStemSeparationJob(jobId: string) { return this.request<{ ok: boolean; jobId: string; status: string }>("POST", `/api/stems/separation/jobs/${jobId}/cancel`); }
  getStemSeparationQualityReport(jobId: string) { return this.request<{ report: unknown }>("GET", `/api/stems/separation/jobs/${jobId}/quality-report`); }
  getStemSeparationManifest(jobId: string) { return this.request<{ manifest: unknown }>("GET", `/api/stems/separation/jobs/${jobId}/manifest`); }
  handoffStemSeparation(jobId: string) { return this.request<{ ok: boolean; jobId: string; handedOffStemCount: number }>("POST", `/api/stems/separation/jobs/${jobId}/handoff-to-stem-intelligence`); }
  setStemOutputApproval(outputId: string, approvalState: string, reviewNote?: string) {
    return this.request<{ ok: boolean; outputId: string; approvalState: string }>("POST", `/api/stems/separation/outputs/${outputId}/approval`, { approvalState, reviewNote });
  }

  // ---- AI Video Generation Engine (22B) ----
  estimateAIVideo(input: { projectId: string; outputType?: string; aspectRatio?: string; durationMs?: number; format?: string; qualityMode?: string; variantCount?: number; preferredModelId?: string }) {
    return this.request<{
      requestId: string; estimatedCreditCost: number; estimatedDurationMs: number; warnings: string[];
      resolvedModelId?: string; modelResolutionWarnings: string[]; modelResolutionBlockers: string[];
      renderCreditsRemaining: number; creditCheck: { allowed: boolean; reason: string };
    }>("POST", "/api/ai/video/estimate", input);
  }
  createAIVideoRequest(input: {
    projectId: string; outputType: string; aspectRatio: string; durationMs: number; format: string; qualityMode: string; variantCount: number;
    userConfirmedRightsWarning: boolean; userConfirmedCreditUsage: boolean;
    releaseId?: string; trackId?: string; setId?: string; brandWorldId?: string;
    visualSceneIds?: string[]; sourceAssetIds?: string[]; sourceAudioRef?: string;
    promptBundleId?: string; userBrief?: string; providerId?: string;
  }) {
    return this.request<{ ok: boolean; requestId: string }>("POST", "/api/ai/video/requests", input);
  }
  getAIVideoRequest(requestId: string) { return this.request<{ request: unknown }>("GET", `/api/ai/video/requests/${requestId}`); }
  createAIVideoJob(requestId: string, preferredModelId?: string) {
    return this.request<{ job: unknown; modelResolutionWarnings: string[] }>("POST", "/api/ai/video/jobs", { requestId, preferredModelId });
  }
  listAIVideoJobs(projectId?: string) {
    return this.request<{ jobs: unknown[] }>("GET", `/api/ai/video/jobs${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`);
  }
  getAIVideoJob(jobId: string) { return this.request<{ job: unknown; outputs: unknown[] }>("GET", `/api/ai/video/jobs/${jobId}`); }
  cancelAIVideoJob(jobId: string) { return this.request<{ ok: boolean; jobId: string; status: string }>("POST", `/api/ai/video/jobs/${jobId}/cancel`); }
  setAIVideoOutputApproval(outputId: string, reviewStatus: string) {
    return this.request<{ ok: boolean; outputId: string; reviewStatus: string }>("POST", `/api/ai/video/outputs/${outputId}/approval`, { reviewStatus });
  }
  checkStemExport(jobId: string, exportType: string) {
    return this.request<{
      ok: boolean; jobId: string; exportType: string; allAllowed: boolean; blockers: string[];
      evaluations: Array<{ exportType: string; rightsState: string; decision: string; reason: string }>;
      excludedCount: number;
    }>("POST", `/api/stems/separation/jobs/${jobId}/export-check`, { exportType });
  }

  // ---- platform OAuth connections (prompts 113/114, task #36) ----
  listOAuthConnections() {
    return this.request<{
      connected: Array<{ provider: string; grantedAt: string; scopes: string[] }>;
      unavailable: Array<{ provider: string; reason: string }>;
    }>("GET", "/api/oauth/connections");
  }
  disconnectOAuth(provider: string) { return this.request<{ ok: boolean; provider: string }>("DELETE", `/api/oauth/${provider}`); }
  /** Not a fetch — the connect flow is a browser redirect/popup to the provider's consent screen. */
  oauthConnectUrl(provider: string): string { return `${this.baseUrl}/api/oauth/${provider}/start`; }

  // ---- Audience Feedback Intelligence / Set Learning Engine (prompt 150) ----
  feedbackPlatformStatus() {
    return this.request<{
      platforms: Array<{ platform: string; authMode: string; status: string; note?: string }>;
      recentImportJobs: Array<{ id: string; platform: string; source: string; status: string; startedAt: string; itemsImported: number }>;
    }>("GET", "/api/feedback/platform-status");
  }
  importSoundCloudFeedback(input: unknown) { return this.request<{ importJobId: string; timedCommentCount: number; untimedCommentCount: number; warnings: string[] }>("POST", "/api/feedback/soundcloud/import", input); }
  listSoundCloudTimedComments(soundCloudTrackId: string) { return this.request<{ items: Array<{ id: string; body: string; timestamp_ms: number; mapped_phrase_id: string | null; sentiment: string; reaction_strength: number; mapping_confidence: number; reactionTags: string[] }> }>("GET", `/api/feedback/soundcloud/${encodeURIComponent(soundCloudTrackId)}/comments`); }
  importYouTubeFeedback(input: unknown) { return this.request<{ importJobId: string; retentionPointCount: number; commentCount: number; warnings: string[] }>("POST", "/api/feedback/youtube/import", input); }
  listYouTubeRetentionPoints(youtubeVideoId: string) { return this.request<{ items: Array<{ id: string; video_time_ms: number; audience_watch_ratio: number | null; mapping_confidence: number }> }>("GET", `/api/feedback/youtube/${encodeURIComponent(youtubeVideoId)}/retention`); }
  feedbackImportJobs() { return this.request<{ items: Array<{ id: string; platform: string; source: string; status: string; started_at: string; items_imported: number; warnings: string[] }> }>("GET", "/api/feedback/import-jobs"); }
  disconnectFeedbackPlatformData(provider: "soundcloud" | "youtube") { return this.request<{ ok: boolean; rowsDeleted: number }>("DELETE", `/api/feedback/platform/${provider}/data`); }

  createLiveFeedbackSession(sessionRef: string) { return this.request<{ id: string; sessionRef: string; feedbackToken: string; submitPath: string }>("POST", "/api/feedback/live/sessions", { sessionRef }); }
  listLiveFeedbackSessions() { return this.request<{ items: Array<{ id: string; session_ref: string; feedback_token: string; created_at: string }> }>("GET", "/api/feedback/live/sessions"); }
  listLiveFeedbackEvents(sessionId: string) { return this.request<{ items: Array<{ id: string; source: string; created_at: string; rating: number | null; body: string | null; sentiment: string; tags: string[] }> }>("GET", `/api/feedback/live/sessions/${sessionId}/events`); }
  submitOperatorNote(sessionId: string, input: { body: string; setTimeMs?: number; trackId?: string; transitionId?: string; isDjNote?: boolean }) {
    return this.request<{ ok: boolean }>("POST", `/api/feedback/live/sessions/${sessionId}/operator-note`, input);
  }
  deleteLiveFeedbackSession(sessionId: string) { return this.request<{ ok: boolean; rowsDeleted: number }>("DELETE", `/api/feedback/live/sessions/${sessionId}`); }
  /** PUBLIC route (no auth) — for the standalone audience QR page, not the DJ workspace. */
  submitLiveAudienceFeedback(token: string, input: { emoji?: string; rating?: number; text?: string; trackRequest?: string; setTimeMs?: number }) {
    return this.request<{ ok: boolean }>("POST", `/api/feedback/live/${token}/submit`, input);
  }
  /** PUBLIC route (no auth) — for the promoter feedback link, not the DJ workspace. */
  submitLivePromoterFeedback(token: string, input: unknown) { return this.request<{ ok: boolean }>("POST", `/api/feedback/live/${token}/promoter`, input); }

  runFeedbackScore(input: { targetType: string; targetId: string; profile?: string; context?: unknown }) {
    return this.request<{ score: DJSEMSetLearningScoreDTO | null }>("POST", "/api/feedback/score/run", input);
  }
  listFeedbackScores(targetType?: string) { return this.request<{ items: DJSEMSetLearningScoreDTO[] }>("GET", `/api/feedback/scores${targetType ? `?targetType=${encodeURIComponent(targetType)}` : ""}`); }
  generateSetLearningReport(input: { sessionId?: string; setId?: string; targets: Array<{ targetType: string; targetId: string }> }) {
    return this.request<{ reportId: string; report: DJSEMSetLearningReportDTO | null }>("POST", "/api/feedback/reports/generate", input);
  }
  latestSetLearningReport(opts: { sessionId?: string; setId?: string }) {
    const qs = new URLSearchParams(opts as Record<string, string>).toString();
    return this.request<{ report: DJSEMSetLearningReportDTO }>("GET", `/api/feedback/reports/latest${qs ? `?${qs}` : ""}`);
  }
  decideFeedbackRecommendation(scoreId: string, decision: "accepted" | "rejected", note?: string) {
    return this.request<{ ok: boolean; decisionId: string; draftSetChangeNote: string | null }>("POST", `/api/feedback/recommendations/${scoreId}/decision`, { decision, note });
  }
  listFeedbackRecommendationDecisions() {
    return this.request<{ items: Array<{ id: string; score_id: string; decision: string; decided_at: string; note: string | null; draft_set_change_note: string | null }> }>("GET", "/api/feedback/recommendations/decisions");
  }
}

/** A scored track/phrase/transition/set-phase/visual-moment, as returned by the feedback scoring routes. */
export interface DJSEMSetLearningScoreDTO {
  id: string; targetType: string; targetId: string;
  soundCloudScore: number | null; youtubeScore: number | null; liveFeedbackScore: number | null;
  combinedScore: number; confidence: number; evidenceCount: number; recommendation: string; reasons: string[]; evidenceIds: string[]; createdAt: string;
}
/** A generated Set Learning Report, as returned by the feedback report routes. */
export interface DJSEMSetLearningReportDTO {
  id: string; sessionId: string | null; setId: string | null; generatedAt: string; scoreIds: string[];
  bestTracks: string[]; weakTracks: string[]; bestPhrases: string[]; weakPhrases: string[]; bestTransitions: string[]; weakTransitions: string[];
  visualFindings: string[]; audienceFindings: string[]; futureSetRecommendations: string[]; confidence: number; limitations: string[];
}
